import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { API_VERSION } from "@utils/types/apiVersion.type";
import { environment } from "@environments/environment.development";
import { WompyCheckoutService } from "@utils/services/wompy-checkout.service";
import { AvailableCurrencies } from "@interfaces/shared";
import { inject, Injectable } from "@angular/core";
import { EpaycoCheckoutService } from "@utils/services/epayco-checkout.service";
import { HttpOptions } from "@utils/interfaces/http.options.model";

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  public checkout = inject(WompyCheckoutService);
  public epaycoCheckout = inject(EpaycoCheckoutService);
  protected URL_API = `${environment.API}`;

  protected URL!: string;

  private readonly _http = inject(HttpClient);

  public getUrl(module: string, version: API_VERSION): string {
    return `${this.URL_API}${module}/${version}/`;
  }

  GET<T>({ url, params, options, context }: {
    url: string,
    params?: HttpParams | Record<string, string>,
    options?: HttpOptions,
    context?: HttpContext
  }): Observable<T> {
    return this._http.get<T>(url, { params, ...options, context });
  }

  public getBlob({
    url,
    params,
    headers,
    context,
  }: {
    url: string;
    params?: HttpParams | Record<string, string>;
    headers?: HttpHeaders | Record<string, string | string[]>;
    context?: HttpContext;
  }): Observable<Blob> {
    return this._http.get(url, { params, headers, context, responseType: 'blob' });
  }

  public postBlob<T>({
    url,
    body,
  }: {
    url: string;
    body: T;
  }): Observable<HttpResponse<Blob>> {
    return this._http.post(url, body, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public getWithId<T>({
    url,
    id,
    params,
    options,
  }: {
    url: string;
    id: string | number;
    params?: HttpParams | Record<string, string>; options?: HttpOptions
  }): Observable<T> {
    return this._http.get<T>(`${url}/${id}`, {
      params,
      ...options,
    });
  }

  public create<T, U>(
    body: T,
    params?: HttpParams,
  ): Observable<U> {
    return this._http.post<U>(this.URL, body, {
      params
    });
  }

  POST<T>({
    url,
    body,
    params,
    options,
    context,
  }: {
    url: string;
    body: unknown;
    params?: HttpParams | Record<string, string>;
    options?: HttpOptions;
    context?: HttpOptions['context'];
  }): Observable<T> {
    return this._http.post<T>(url, body, {
      params,
      ...options,
      context: context,
    });
  }

  public createWithId<T, U>(
    url: string,
    id: number | string,
    body: T,
    params?: HttpParams
  ): Observable<U> {
    return this._http.post<U>(`${url}/${id}`, body, {
      params
    });
  }

  public listWithId<T>({
    url,
    id,
    params,
    options,
  }: {
    url: string;
    id: string | number;
    params?: HttpParams | Record<string, string>;
    options?: HttpOptions;
  }): Observable<T> {
    return this._http.get<T>(`${url}/${id}`, {
      params,
      ...options,
    });
  }

  PATCH<T>({
    url,
    body,
    params,
    options,
  }: {
    url: string;
    body: unknown;
    params?: HttpParams | Record<string, string>;
    options?: HttpOptions;
  }): Observable<T> {
    return this._http.patch<T>(url, body, { params, ...options });
  }

  PUT<T>({
    url,
    body,
    params,
    options,
  }: {
    url: string;
    body: unknown;
    params?: HttpParams | Record<string, string>;
    options?: HttpOptions;
  }): Observable<T> {
    return this._http.put<T>(url, body, { params, ...options });
  }

  listPagination<T>(
    page: number,
    size: number,
    params?: HttpParams | Record<string, string>,
    url?: string
  ): Observable<T> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('range', this._calculatePageRange(page, size));

    return this._http.get<T>(url ? url : this.URL, {
      headers,
      params
    });
  }

  public listPaginationHttpResponse<T>(
    page: number,
    size: number,
    params?: HttpParams | Record<string, string>,
    url?: string,
  ): Observable<HttpResponse<T>> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('range', this._calculatePageRange(page, size));

    return this._http.get<T>(url ? url : this.URL, {
      headers,
      params,
      observe: 'response'
    });
  }

  public listPaginationWithID<T>(
    id: number,
    page: number,
    size: number,
    params?: HttpParams
  ): Observable<T> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('range', this._calculatePageRange(page, size));

    return this._http.get<T>(`${this.URL}/${encodeURIComponent(id)}`, {
      headers,
      params
    });
  }

  public updatePartial<T, U>(
    url: string,
    id: string | number,
    body?: T,
    params?: HttpParams
  ): Observable<U> {
    return this._http.patch<U>(`${url}/${encodeURIComponent(id)}`, body, { params });
  }

  public updateTotal<T, U>(
    id: string | number,
    url: string,
    body?: T,
    params?: HttpParams
  ): Observable<U> {
    return this._http.put<U>(`${url}/${encodeURIComponent(id)}`, body, { params });
  }

  DELETE<T>({
    url,
    params,
    options,
  }: {
    url: string;
    params?: HttpParams | Record<string, string>;
    options?: HttpOptions;
  }): Observable<T> {
    return this._http.delete<T>(url, {
      params,
      ...options,
    });
  }

  private _calculatePageRange(
    page: number,
    size: number
  ): string {
    const startRange = page * size - size;
    const endRange = page * size - 1;
    return `${startRange}-${endRange}`;
  }

  public createPaymentWompi(
    currency: AvailableCurrencies,
    paymentRef: string,
    price: number
  ): void {
    this.checkout.create({ price, currency, paymentRef });
  }

  public createPaymentEpayco(
    price: number,
    ref: string,
    paymentName: string,
    paymentDescription: string,
    showCashOption: boolean
  ): void {
    this.epaycoCheckout.pay({
      paymentDescription,
      paymentName,
      showCashOption,
      inserted: ref,
      price
    });
  }
}
